<script>
import { on, off, offset, getWindowScrollPosition } from '@v4/common/utils/dom';
import throttle from 'lodash/throttle';
const STICKY_NONE = 'none';
const STICKY_MONOAXIAL = 'monoaxial';
const STICKY_BIAXIAL = 'biaxial';
const STICKY_HEADERS_ALLOWED_VALUES = [STICKY_NONE, STICKY_MONOAXIAL, STICKY_BIAXIAL];

/**
 * Table component
 *
 * @version 4.0.0
 * @example ./examples/DkTable.md
 */
export default {
    name: 'DkTable',
    props: {
        /**
         * Sticky headers axes. Choices available: `none`, `monoaxial`, `biaxial`.
         */
        stickyHeaders: {
            type: String,
            default: STICKY_NONE,
            validator(value) {
                return STICKY_HEADERS_ALLOWED_VALUES.includes(value);
            },
        },
        /**
         * Will disable sticky headers on specific row number starting counting from the end.
         */
        rowsAllowance: {
            type: Number,
            default: 3,
        },
        /**
         * Appends additional classes to the table tag
         */
        tableClasses: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            monoaxialIsSticky: false,
            biaxialIsSticky: false,
            isScrollingFast: false,
            mutationObserver: null,
            previousTop: 0,
            scrollTimerId: 0,
        };
    },
    computed: {
        isMonoaxial() {
            return (
                STICKY_HEADERS_ALLOWED_VALUES.includes(this.stickyHeaders) && this.stickyHeaders === STICKY_MONOAXIAL
            );
        },
        isBiaxial() {
            return STICKY_HEADERS_ALLOWED_VALUES.includes(this.stickyHeaders) && this.stickyHeaders === STICKY_BIAXIAL;
        },
    },
    mounted() {
        if (this.isMonoaxial || this.isBiaxial) {
            on(this.$el, 'scroll', this.onTableHorizontalScroll);
            on(window, 'load', this.setCellWidths);
            on(window, 'scroll', this.onWindowScroll);
            on(window, 'resize', this.throttledWindowResize);

            this.mutationObserver = new MutationObserver(this.throttledWindowScroll);
            this.mutationObserver.observe(this.$refs.theadElm, { attributes: true, childList: true, subtree: true });
        }

        if (this.isBiaxial) {
            this.removeCells();
        }
    },
    destroyed() {
        if (this.isMonoaxial || this.isBiaxial) {
            off(this.$el, 'scroll', this.onTableHorizontalScroll);
            off(window, 'load', this.setCellWidths);
            off(window, 'scroll', this.onWindowScroll);
            off(window, 'resize', this.throttledWindowResize);

            if (this.mutationObserver) {
                this.mutationObserver.disconnect();
            }
        }
    },
    methods: {
        throttledWindowResize: throttle(function() {
            this.onWindowResize();
        }, 250),
        throttledWindowScroll: throttle(function() {
            this.onWindowScroll();
        }, 250),
        repositionStickyHead() {
            const allowance = this.calcRowsAllowance();
            const windowScrollYPosition = getWindowScrollPosition();
            const tableElmOffsetTop = offset(this.$refs.tableElm).top;

            // Check if wrapper parent is overflowing along the y-axis
            if (this.$refs.tableElm.offsetHeight > this.$el.offsetHeight) {
                // When top of wrapping parent is out of view
                if (this.$el.scrollTop > 0) {
                    // this.$refs.stickyHeadElm.style.opacity = 1;
                    this.$refs.stickyHeadElm.style.transform = `translateY(${this.$el.scrollTop.scrollTop}px)`;

                    if (this.isBiaxial) {
                        this.$refs.stickyIntersectElm.style.opacity = 1;
                        this.$refs.stickyIntersectElm.style.transform = `translateY(${this.$el.scrollTop.scrollTop}px)`;
                    }
                } else {
                    // When top of wrapping parent is in view
                    // this.$refs.stickyHeadElm.style.opacity = 0;
                    this.$refs.stickyHeadElm.style.transform = 'translateY(0)';

                    if (this.isBiaxial) {
                        this.$refs.stickyIntersectElm.style.opacity = 0;
                        this.$refs.stickyIntersectElm.style.transform = 'translateY(0)';
                    }
                }
            } else {
                // If it is not overflowing
                // Position sticky header based on viewport scrollTop
                if (
                    windowScrollYPosition > tableElmOffsetTop &&
                    windowScrollYPosition < tableElmOffsetTop + this.$refs.tableElm.offsetHeight - allowance
                ) {
                    // When top of viewport is in the table itself
                    // this.$refs.stickyHeadElm.style.opacity = 1;
                    this.$refs.stickyHeadElm.style.transform = `translateY(${windowScrollYPosition -
                        tableElmOffsetTop}px)`;

                    if (this.isBiaxial) {
                        this.$refs.stickyIntersectElm.style.opacity = 1;
                        this.$refs.stickyIntersectElm.style.transform = `translateY(${windowScrollYPosition -
                            tableElmOffsetTop}px)`;
                    }

                    this.monoaxialIsSticky = true;
                } else {
                    // When top of viewport is above or below table
                    // this.$refs.stickyHeadElm.style.opacity = 0;
                    this.$refs.stickyHeadElm.style.transform = 'translateY(0)';

                    if (this.isBiaxial) {
                        this.$refs.stickyIntersectElm.style.opacity = 0;
                        this.$refs.stickyIntersectElm.style.transform = 'translateY(0)';
                    }

                    this.monoaxialIsSticky = false;
                }
            }
        },
        repositionStickyCol() {
            if (this.$el.scrollLeft > 0) {
                // When left of wrapping parent is out of view
                this.$refs.stickyColElm.style.opacity = 1;
                this.$refs.stickyColElm.style.transform = `translateX(${this.$el.scrollLeft}px)`;

                this.$refs.stickyIntersectElm.style.opacity = 1;
                this.$refs.stickyIntersectElm.style.transform = `translateX(${this.$el.scrollLeft}px)`;

                this.biaxialIsSticky = true;
            } else {
                // When left of wrapping parent is in view
                this.$refs.stickyColElm.style.opacity = 0;
                this.$refs.stickyIntersectElm.style.transform = 'translateX(0px)';

                this.biaxialIsSticky = false;
            }
        },
        setCellWidths() {
            const tableTheadThElms = this.$refs.tableElm.querySelectorAll('thead th');
            const stickyHeadThElms = this.$refs.stickyHeadElm.querySelectorAll('th');

            for (let i = 0; i < tableTheadThElms.length; i += 1) {
                const thElm = tableTheadThElms[i];
                const stickyHeadThElm = stickyHeadThElms[i];

                stickyHeadThElm.style.width = `${thElm.offsetWidth}px`;

                // update also all attributes
                for (let j = 0; j < thElm.attributes.length; j += 1) {
                    const attribute = thElm.attributes[j];

                    stickyHeadThElm.setAttribute(attribute.nodeName, attribute.nodeValue);
                }
            }

            const tableTheadTrElms = this.$refs.tableElm.querySelectorAll('thead tr');
            const stickyHeadTrElms = this.$refs.stickyHeadElm.querySelectorAll('tr');

            for (let i = 0; i < tableTheadTrElms.length; i += 1) {
                stickyHeadTrElms[i].style.height = `${tableTheadTrElms[i].offsetHeight}px`;
            }

            // Set width of sticky table head
            this.$refs.stickyHeadElm.style.width = `${this.$refs.tableElm.offsetWidth}px`;

            // Set width of sticky table col
            if (this.isBiaxial) {
                this.$refs.stickyColElm.querySelector('th').style.width = `${tableTheadThElms[0].offsetWidth}px`;
                this.$refs.stickyIntersectElm.querySelector('th').style.width = `${tableTheadThElms[0].offsetWidth}px`;
            }
        },
        onWindowResize() {
            this.setCellWidths();
            this.repositionStickyHead();

            if (this.isBiaxial) {
                this.repositionStickyCol();
            }
        },
        onWindowScroll() {
            this.repositionStickyHead();
            this.setCellWidths();
            this.handleScrollingDetection();
        },
        onTableHorizontalScroll() {
            this.repositionStickyHead();

            if (this.isBiaxial) {
                this.repositionStickyCol();
            }
        },
        /**
         * Calculates allowance.
         *
         * We do not want the sticky table header to follow us all the way to the end
         * of the table. We are avoiding the risk of obfuscating the last table row.
         *
         * @returns {number} allowance - return value of calculated allowance
         */
        calcRowsAllowance() {
            let allowance = 0;

            if (!this.$refs.tableElm || !this.$refs.stickyHeadElm) {
                return allowance;
            }

            const arbitraryLimit = 0.25;
            const tableTbodyTrElms = this.$refs.tableElm.querySelectorAll('tbody tr');
            const windowHeight =
                window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;

            for (let i = 0; i < tableTbodyTrElms.length; i += 1) {
                const tr = tableTbodyTrElms[i];

                if (i > this.rowsAllowance - 1) {
                    continue;
                }

                allowance += tr.offsetHeight;
            }

            // Set fail safe limit (last three row might be too tall)
            // Set arbitrary limit at 0.25 of viewport height, or you can use an arbitrary pixel value
            if (allowance > windowHeight * arbitraryLimit) {
                allowance = windowHeight * arbitraryLimit;
            }

            allowance += this.$refs.stickyHeadElm.offsetHeight;

            return allowance;
        },
        removeCells() {
            if (!this.$refs.stickyColElm || !this.$refs.stickyIntersectElm || !this.$refs.tableElm) {
                return false;
            }

            // Remove all thead th, except the first one
            const theadThElms = this.$refs.stickyColElm.querySelectorAll('thead th');
            for (let i = 0; i < theadThElms.length; i += 1) {
                const th = theadThElms[i];

                if (i > 0) {
                    th.parentNode.removeChild(th);
                }
            }

            // Remove all tbody td
            const tbodyTdElms = this.$refs.stickyColElm.querySelectorAll('tbody td');
            for (let i = 0; i < tbodyTdElms.length; i += 1) {
                const td = tbodyTdElms[i];
                td.parentNode.removeChild(td);
            }

            this.$refs.stickyIntersectElm.innerHTML = `
                <thead>
                    <tr>
                        <th>${this.$refs.tableElm.querySelector('thead th:first-child').innerHTML}</th>
                    </tr>
                </thead>`;

            return true;
        },
        handleScrollingDetection() {
            if (!window.requestAnimationFrame) {
                setTimeout(this.scrollingDetection, 250);
            } else {
                requestAnimationFrame(this.scrollingDetection);
            }
        },
        scrollingDetection() {
            if (!this.isMonoaxial) {
                return;
            }

            const currentTop = window.pageYOffset;
            const scrollDelta = 0; // the higher it is, the faster you have to scroll

            if (
                this.previousTop - currentTop > scrollDelta || // scrolling up
                (currentTop - this.previousTop > scrollDelta && currentTop > 150) // scrolling down
            ) {
                clearTimeout(this.scrollTimerId);
                this.isScrollingFast = true;
                this.scrollTimerId = setTimeout(() => {
                    this.isScrollingFast = false;
                }, 100);
            }

            this.previousTop = currentTop;
        },
    },
    render(h) {
        const childrenVNodes = [];
        const tableBaseClass = `table table--layout_auto ${this.tableClasses}`;
        const theadBaseClass = 'table__head';
        const tbodyBaseClass = 'table__body';

        if (this.isMonoaxial || this.isBiaxial) {
            childrenVNodes.push(
                h('table', { ref: 'stickyHeadElm', class: `${tableBaseClass} dk-table__sticky-head` }, [
                    h('thead', this.$slots.thead),
                ])
            );
        }

        if (this.isBiaxial) {
            childrenVNodes.push(
                h('table', { ref: 'stickyIntersectElm', class: `${tableBaseClass} dk-table__sticky-intersect` }, [
                    h('thead', { class: theadBaseClass }, this.$slots.thead),
                ])
            );

            childrenVNodes.push(
                h('table', { ref: 'stickyColElm', class: `${tableBaseClass} dk-table__sticky-col` }, [
                    h('thead', { class: theadBaseClass }, this.$slots.thead),
                    h('tbody', { class: tbodyBaseClass }, this.$slots.default),
                ])
            );
        }

        return h(
            'div',
            {
                class: {
                    'dk-table': true,
                    'dk-table--scrolling-fast': this.isScrollingFast,
                    'dk-table--has-sticky-headers': this.isMonoaxial || this.isBiaxial,
                    [`dk-table--is-sticky_${STICKY_MONOAXIAL}`]: this.monoaxialIsSticky,
                    [`dk-table--is-sticky_${STICKY_BIAXIAL}`]: this.biaxialIsSticky,
                },
            },
            [
                ...childrenVNodes,
                h('table', { ref: 'tableElm', class: `${tableBaseClass} dk-table__table` }, [
                    h('thead', { ref: 'theadElm', class: theadBaseClass }, this.$slots.thead),
                    h('tbody', { class: tbodyBaseClass }, this.$slots.default),
                    ...(this.$slots.tfoot ? [h('tfoot', { class: 'table__foot' }, this.$slots.tfoot)] : []),
                ]),
            ]
        );
    },
};
</script>
